import { useCallback, useState, useMemo } from 'react'
import { Grid, Stack } from '@chakra-ui/layout'
import { Box, Radio, RadioGroup, FormControl, FormLabel } from '@chakra-ui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  CreditCardForm,
  PlanForm,
  AddressForm,
  OrderSummary,
  PackageForm,
} from '../../components'

import { ManualPaymentInstructions } from '../../components/ManualPaymentInstructions'
import { useOrderCalculation } from '../../components/hooks/useOrderCalculation'
import { FreeTrialCoupon } from '../../mutations'

import { PACKAGE_NAMES_IN_USE } from '@/features/payments/constants'
import {
  OVERRIDE_STRIPE_STYLES,
  STRIPE_ELEMENTS,
  TAX_PAYER_TYPES,
} from '../../constants'
import { makeFreeTrialFormSchema } from '../../schema'

import { useSubmitFreeTrial } from '../../mutations'
import { getQueryParam } from '@/lib/queryParams'

import {
  FreeTrialFormInput,
  StripeElementErrors,
  StripeElementString,
} from '../../types'

const defaultErrors: StripeElementErrors = {
  card_number: '',
  card_expiry: '',
  card_cvc: '',
}

type PaymentType = 'credit_card' | 'manual'

export const FreeTrialFormContainer = (): React.ReactElement => {
  const stripe = useStripe()
  const elements = useElements()

  const { t, i18n } = useTranslation(['freeTrial'])

  const schema = useMemo(
    () => makeFreeTrialFormSchema(t),
    [t, i18n.language],
  )

  const packageNameQuery = getQueryParam('package') as any

  const [errors, setErrors] = useState(defaultErrors)
  const [paymentType, setPaymentType] = useState<PaymentType>('credit_card')

  const [applyCoupon, setApplyCoupon] = useState<FreeTrialCoupon | undefined>()

  const method = useForm<FreeTrialFormInput>({
    defaultValues: {
      package: PACKAGE_NAMES_IN_USE.includes(packageNameQuery)
        ? packageNameQuery
        : 'small',
      recurring: 'year',
      taxpayer_type: TAX_PAYER_TYPES[0],
    },
    resolver: zodResolver(schema),
  })
  const { handleSubmit, control } = method

  const packageName = useWatch({ control, name: 'package' })
  const recurring = useWatch({ control, name: 'recurring' })

  const { priceObj, dueDate } = useOrderCalculation({
    packageName,
    recurring,
    applyCoupon,
  })

  const { mutate, isLoading } = useSubmitFreeTrial()

  const validateStripeField = useCallback((className: StripeElementString) => {
    setErrors(defaultErrors)

    let element = document.querySelector(`.${className}.StripeElement--empty`)

    if (element) {
      let errorMsg = ''
      switch (className) {
        case 'card_number': {
          errorMsg = t('form.errors.numberRequired')
          break
        }
        case 'card_expiry': {
          errorMsg = t('form.errors.expiryRequired')
          break
        }
        case 'card_cvc': {
          errorMsg = t('form.errors.cvcRequired')
          break
        }
      }

      setErrors((state) => ({ ...state, [className]: errorMsg }))

      return false
    }

    let element2 = document.querySelector(
      `.${className}.StripeElement--invalid`,
    )

    if (element2) {
      let errorMsg = ''
      switch (className) {
        case 'card_number': {
          errorMsg = t('form.errors.numberInvalid')
          break
        }
        case 'card_expiry': {
          errorMsg = t('form.errors.expiryInvalid')
          break
        }
        case 'card_cvc': {
          errorMsg = t('form.errors.cvcInvalid')
          break
        }
      }

      setErrors((state) => ({ ...state, [className]: errorMsg }))

      return false
    }

    return true
  }, [t])

  return (
    <FormProvider {...method}>
      <form
        onSubmit={handleSubmit((data) => {
          if (paymentType === 'manual') {
            mutate({
              data: {
                form: { ...data, payment_type: 'manual' },
                number: null,
                expiry: null,
                cvc: null,
              },
            })
            return
          }

          if (!stripe || !elements) {
            return
          }

          const number = elements.getElement(CardNumberElement)
          const expiry = elements.getElement(CardExpiryElement)
          const cvc = elements.getElement(CardCvcElement)

          let isPassed = true

          for (const element of STRIPE_ELEMENTS) {
            if (!validateStripeField(element)) {
              isPassed = false
              break
            }
          }

          if (!isPassed) return

          mutate({
            data: {
              form: data,
              number,
              expiry,
              cvc,
            },
          })
        })}
      >
        <Grid
          sx={{
            gridTemplateColumns: '1fr 440px',
            gap: '16px',
            alignItems: 'flex-start'
          }}
        >
          <Stack
            sx={{
              p: 6,
              border: '1px solid',
              borderColor: 'gray.200',
              borderRadius: 'lg',
              ...OVERRIDE_STRIPE_STYLES,
            }}
            spacing={6}
          >
            <PackageForm />
            <PlanForm />

            <FormControl>
              <FormLabel fontWeight="normal">
                {t('form.paymentMethod.title')}
              </FormLabel>
              <RadioGroup
                value={paymentType}
                onChange={(val) => setPaymentType(val as PaymentType)}
              >
                <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
                  <Radio value="credit_card">{t('form.paymentMethod.withCreditDebit')}</Radio>
                  <Radio value="manual">{t('form.paymentMethod.withBankTransfer')}</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            {paymentType === 'credit_card' && <CreditCardForm errors={errors} />}

            <AddressForm />

            {paymentType === 'manual' && (
              <ManualPaymentInstructions
                finalPrice={priceObj.finalPrice}
                dueDate={dueDate.formatted}
              />
            )}
          </Stack>

          <OrderSummary
            isLoading={isLoading}
            applyCoupon={applyCoupon}
            setApplyCoupon={setApplyCoupon}
          />
        </Grid>
      </form>
    </FormProvider>
  )
}
